import { z } from "zod";

/**
 * ROLE SCOPE — the second global scope, beside the organization.
 *
 * The organization the request acts on rides the Clerk JWT (`org_id`), so it is
 * signed and the backend never has to check it. A Role is our own primitive and
 * Clerk has no concept of it, so it cannot ride the same rail. It travels as
 * this header instead — which means it is CALLER-SUPPLIED and unsigned, and the
 * whole of this file exists to make that harmless.
 *
 * ## The name
 *
 * `role` is already taken in this codebase and means something else:
 * `OrganizationRole` (`apps/frontend/src/common/layouts/MainLayout/types.ts`) is
 * Clerk's org membership role — `"org:admin" | "org:member"` — and every Settings
 * navigation item carries a `roles: OrganizationRole[]` field. The JWT carries an
 * `org_role` claim for the same concept. Nothing here is ever called `role` on its
 * own: it is `roleScope` everywhere, in the header, in the decorator, in the hook
 * and in the query key.
 *
 * ## Why the value never escapes as a bare string
 *
 * A bare `roleId: string` handed to a handler is one careless line away from
 * becoming the tenancy key — `where: { roleId }`, or worse, a lookup that reads
 * the role's own `organizationId` and then trusts it. So this module never yields
 * one. It yields PREDICATE FRAGMENTS, and the shape of a fragment is the
 * guarantee: `{ roleId?: string }` cannot express an organization, so it can only
 * ever narrow a query that already carries `organizationId` from the JWT.
 *
 *     where: { organizationId, ...scope.byRoleId }   // narrows, never widens
 *
 * A role id belonging to another organization therefore selects no rows — exactly
 * what a role id that does not exist selects, and exactly what a deleted one
 * selects. There is no third answer to leak, no 500, and no code path that has to
 * remember to check. Where a route needs to say "that role is gone" out loud
 * rather than return an empty list, `assertRoleIsInOrganization`
 * (`apps/backend/src/roles/application/use-cases/`) is the one place that decides
 * it; this module deliberately does not duplicate that.
 *
 * ## "All roles" is the ABSENCE of the header, never a sentinel
 *
 * A roleless organization sends no header. A user who has selected "All roles"
 * sends no header. The two produce the same empty fragments and therefore the
 * same SQL, so there are not two code paths to keep in step and they cannot
 * drift. A sentinel value (`role=all`) would create the second path on day one.
 */

/**
 * The wire name, lowercase because Node lowercases every incoming header name and
 * a mixed-case literal would silently miss on the server.
 *
 * 🚨 A new custom request header is NOT reachable from a browser until it is also
 * named in `allowedHeaders` of `app.enableCors(...)` in `apps/backend/src/main.ts`.
 * That allowlist is explicit. Omitting it fails the CORS preflight in the browser
 * and nowhere else: jest, vitest, curl, SSR and `node dist/main --smoke` all pass.
 */
export const ROLE_SCOPE_HEADER = "x-nexus-role-scope";

/**
 * `Role.id` is a `@db.Uuid`. Format only — this asserts nothing about the role
 * existing or belonging to the caller, and it is not meant to: the fragments
 * below are what make a foreign id harmless.
 */
export const RoleScopeIdSchema = z.string().uuid();

/**
 * A resolved role scope, as two Prisma predicate fragments.
 *
 * Both are `{}` for "All roles", so a consumer spreads them unconditionally and
 * the scoped and unscoped paths are the same line of code.
 */
export type RoleScope = Readonly<{
  /** For a table carrying a `roleId` column — `RoleScopeLine`, `RoleResource`, … */
  byRoleId: Readonly<{ roleId?: string }>;
  /** For the `Role` table itself, whose own column is `id`. */
  byId: Readonly<{ id?: string }>;
}>;

/** "All roles". Frozen, and the same object every time, so it is cheap to compare. */
export const ALL_ROLES: RoleScope = Object.freeze({
  byRoleId: Object.freeze({}),
  byId: Object.freeze({})
});

/** Whether a scope narrows anything at all. `false` for "All roles". */
export function isRoleScoped(scope: RoleScope): boolean {
  return scope.byRoleId.roleId !== undefined;
}

/**
 * Build the fragments for one role id.
 *
 * The key is OMITTED rather than set to `undefined` in the unscoped case. Prisma
 * drops `undefined` keys from a `where`, so the two behave identically today — but
 * a fragment that literally has no `roleId` key cannot be misread by a human or by
 * a future `exactOptionalPropertyTypes` as "filter on nothing".
 */
export function roleScopeFor(roleId: string): RoleScope {
  return Object.freeze({
    byRoleId: Object.freeze({ roleId }),
    byId: Object.freeze({ id: roleId })
  });
}
